import { Component } from 'react'
import { IconButton, InputAdornment, TextField, FormControl, InputLabel, Grid } from '@mui/material'
import { styled, textFieldClasses, Box, Select, MenuItem, Typography, colors, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { WrapIcon } from './units'
import { getSocialLinkConfig } from './unit.icons'
import { ESocialLinkType, ISocialLink, ISocialLinkData } from './types'
import { CopyToClipboard } from '../../components/copy-to-clipboard'
import { isValidURL, tryParseObject } from '../../utils/helpers'
import { IFormInputBase } from '../../form/types'
import { getErrorMessage } from '../../form/helpers'
import { structureVersion } from './configs'

export const inputSocialLinksClasses = {
  root: 'inputSocialLinks-root',
  linkRows: 'inputSocialLinks-linkRows',
  linkRow: 'inputSocialLinks-linkRow',
  titleLinkRow: 'inputSocialLinks-titleLinkRow',
  input: 'inputSocialLinks-input'
}

export interface IInputSocialLinksProps<T> extends IFormInputBase<T> {}

export interface IInputSocialLinksState {
  links: ISocialLink[]
  currentType: ESocialLinkType
  currentInput: string
}

export function createInputSocialLinks<T>() {
  class InputSocialLinks extends Component<IInputSocialLinksProps<T>, IInputSocialLinksState> {
    constructor(props: IInputSocialLinksProps<T>) {
      super(props)
      // Initialize state with existing links from props or empty array
      // Make sure we adapt incoming links to our ISocialLink interface
      this.state = { links: this.getDefaultLinks(), currentInput: '', currentType: ESocialLinkType.Website }
    }

    render() {
      const { links, currentInput } = this.state
      const { name, label, messageErrors } = this.props
      const errorMessage = getErrorMessage(messageErrors, name)

      return (
        <Wrap container spacing={2} className={inputSocialLinksClasses.root}>
          <input type='hidden' name={name?.toString()} value={this.getInputValue()} />
          <Grid item xs={3}>
            <FormControl fullWidth>
              <InputLabel id='social-link-type-label'>Link Type</InputLabel>
              <Select labelId='social-link-type-label' value={this.state.currentType} label='Link Type' onChange={this.handleTypeChange}>
                {Object.values(ESocialLinkType).map((type) => {
                  const config = getSocialLinkConfig(type)
                  const IconComp = config.Icon
                  return (
                    <MenuItem key={type} value={type}>
                      <MenuItemCustom>
                        <WrapIcon size={24} sx={{ bgcolor: config.primaryColor }}>
                          <IconComp fontSize='small' sx={{ color: config.secondaryColor ?? colors.common.white }} />
                        </WrapIcon>
                        <Typography variant='body2' noWrap>
                          {config.label ?? type}
                        </Typography>
                      </MenuItemCustom>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={9}>
            <TextField
              fullWidth
              className={inputSocialLinksClasses.input}
              label={label}
              error={!!errorMessage.error}
              helperText={errorMessage.message}
              value={currentInput}
              onChange={this.handleInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  this.handleAddLink()
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <Tooltip title='Add Link' arrow>
                      <IconButton size='small' onClick={this.handleAddLink}>
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box className={inputSocialLinksClasses.linkRows}>
              {links.map((link, index) => {
                const config = getSocialLinkConfig(link.Type)
                const IconComp = config.Icon
                return (
                  <div className={inputSocialLinksClasses.linkRow} key={index}>
                    <WrapIcon sx={{ bgcolor: config.primaryColor }}>
                      <IconComp sx={{ color: config.secondaryColor ?? colors.common.white }} />
                    </WrapIcon>
                    <Typography variant='body2' noWrap className={inputSocialLinksClasses.titleLinkRow}>
                      {link.Url}
                    </Typography>
                    <Tooltip title='Open Link' arrow>
                      <IconButton size='small' href={link.Url} target='_blank' rel='noopener noreferrer'>
                        <OpenInNewIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <CopyToClipboard value={link.Url} />
                    <Tooltip title='Delete Link' arrow>
                      <IconButton size='small' onClick={() => this.handleDeleteLink(link)}>
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                )
              })}
            </Box>
          </Grid>
        </Wrap>
      )
    }

    getInputValue = () => {
      const { links } = this.state
      return JSON.stringify({ StructureVersion: structureVersion, Items: links })
    }

    getDefaultLinks = () => {
      const { data, name } = this.props
      const val = name ? data?.[name] : ''
      const obj = tryParseObject<ISocialLinkData>(val, { StructureVersion: structureVersion, Items: [] })
      return Array.isArray(obj?.Items) ? obj.Items : []
    }

    handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({ currentInput: e.target.value })
    }

    handleTypeChange = (event: any) => {
      this.setState({ currentType: event.target.value as ESocialLinkType })
    }

    handleAddLink = () => {
      const { currentInput, links, currentType } = this.state
      const trimmedInput = currentInput.trim()

      if (!trimmedInput) return

      // Check if link already exists
      if (links.some((link) => link.Url === trimmedInput)) {
        // Could add error state here if needed
        const { formContext, name } = this.props
        if (name) formContext?.setError(name, 'This link already exists')
        return
      }

      // Validate URL
      if (!isValidURL(trimmedInput)) {
        // Set error state or show message
        const { formContext, name } = this.props
        if (name) formContext?.setError(name, 'Invalid URL')
        return
      }

      const newLink: ISocialLink = { Url: trimmedInput, Type: currentType }

      const newLinks = [...links, newLink]
      this.setState({ links: newLinks, currentInput: '' }, () => {
        const { formContext, name } = this.props
        if (name) formContext?.removeError(name)
      })
    }

    handleDeleteLink = (linkToDelete: ISocialLink) => {
      const newLinks = this.state.links.filter((link) => link.Url !== linkToDelete.Url)
      this.setState({ links: newLinks })
    }
  }

  return InputSocialLinks
}
export default createInputSocialLinks

const Wrap = styled(Grid)(({ theme }) => ({
  // [`& .${inputSocialLinksClasses.root}`]: {}
  [`& .${inputSocialLinksClasses.linkRows}`]: {
    overflowY: 'auto',
    maxHeight: 'calc(var(--isl-row-height, 40px) * 5)'
  },
  [`& .${inputSocialLinksClasses.linkRow}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '0 12px',
    marginBottom: '8px',
    backgroundColor: colors.grey[50],
    borderRadius: theme.shape.borderRadius,
    height: 'var(--isl-row-height, 40px)',
    '&:hover': {
      backgroundColor: colors.grey[100]
    }
  },
  [`& .${inputSocialLinksClasses.titleLinkRow}`]: {
    marginLeft: '6px',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden'
  },
  [`& .${inputSocialLinksClasses.input} .${textFieldClasses.root}`]: {
    paddingRight: theme.spacing(1)
  }
}))

const MenuItemCustom = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '.MuiTypography-root': {
    lineHeight: 1,
    marginTop: '2px'
  }
})
